import React, { useState, useContext, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu as MuiMenu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Box,
  Tooltip,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import WidgetsIcon from '@mui/icons-material/Widgets';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Brightness4OutlinedIcon from '@mui/icons-material/Brightness4Outlined';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DevicesIcon from '@mui/icons-material/Devices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ThemeContext } from '../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import { useAuth } from '../hooks/useAuth';
import { saveLanguageToProfile } from '../utils/userLanguage';
import secureStorage from '../utils/secureStorage';
import HostingSwitcher from './HostingSwitcher';
import AgentControl from './AgentControl';
import { isHosterOrAbove, isAdminOrSuperAdmin, normalizeRole } from '../utils/roles';
import { useMonitoring } from '../context/MonitoringContext';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const languageOptions = [
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'zh', label: '中文', flag: '🇨🇳' }
];

const Menu = ({ onRefresh, refreshInterval = 60000, hideRefresh = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [refreshMenuAnchor, setRefreshMenuAnchor] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState(refreshInterval); // Используем переданный интервал
  const [isRefreshMenuOpen, setIsRefreshMenuOpen] = useState(false);
  const [isWidgetsPopupOpen, setIsWidgetsPopupOpen] = useState(false); // Состояние для popup
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null); // Состояние для меню уведомлений
  const [notificationsCount, setNotificationsCount] = useState(0); // Количество уведомлений
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [markingNotificationId, setMarkingNotificationId] = useState(null);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const { toggleTheme } = useContext(ThemeContext);
  const { user, logout: authLogout } = useAuth();
  const { disabled: monitoringDisabled } = useMonitoring();
  const { t, i18n } = useTranslation(['menu', 'billing']);
  const isClientBlocked = monitoringDisabled && normalizeRole(user?.role) === 'client';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [adminAnchorEl, setAdminAnchorEl] = useState(null);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefreshMenuOpen = (event) => {
    setRefreshMenuAnchor(event.currentTarget);
  };

  const handleRefreshMenuClose = () => {
    setRefreshMenuAnchor(null);
  };

  const handleRefreshMenuToggle = () => {
    setIsRefreshMenuOpen(!isRefreshMenuOpen);
  };

  const handleRefreshMenuCloseClickAway = () => {
    setIsRefreshMenuOpen(false);
  };

  const handleWidgetsPopupOpen = () => {
    setIsWidgetsPopupOpen(true);
  };

  const handleWidgetsPopupClose = () => {
    setIsWidgetsPopupOpen(false);
  };

  const handleNotificationsMenuOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    fetchMiniNotifications();
  };

  const handleNotificationsMenuClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleNavigateToLogs = () => {
    handleNotificationsMenuClose();
    navigate('/alerts');
  };

  const handleNavigateToSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const handleNavigateToSubscription = () => {
    handleMenuClose();
    navigate('/subscription');
  };

  const handleNavigateToBill = () => {
    handleMenuClose();
    navigate('/bill');
  };

  const handleNavigateToTickets = () => {
    handleMenuClose();
    navigate('/tickets');
  };

  const handleNavigateToPayments = () => {
    handleMenuClose();
    navigate('/payments');
  };

  const fetchMiniNotifications = async () => {
    try {
      // Токен добавляется автоматически, проверка аутентификации также автоматическая
      const resp = await api.get(`${API_BASE_URL}notification_mini`);
      const data = resp?.data || {};
      setNotificationsCount(Number(data.unread_count) || 0);
      setUnreadNotifications(Array.isArray(data.unread_notifications) ? data.unread_notifications : []);
    } catch (e) {
      setUnreadNotifications([]);
      setNotificationsCount(0);
    }
  };

  const getSeverityChipProps = (severity) => {
    switch (severity) {
      case 'critical':
        return { label: t('severityCritical'), bg: 'rgba(229,72,77,0.14)', fg: '#d33c41' };
      case 'important':
        return { label: t('severityImportant'), bg: 'rgba(240,150,20,0.16)', fg: '#b26a00' };
      case 'basic':
        return { label: t('severityBasic'), bg: 'rgba(48,160,88,0.16)', fg: '#1f8a4c' };
      case 'warning':
        return { label: t('severityWarning'), bg: 'rgba(240,150,20,0.16)', fg: '#b26a00' };
      case 'info':
        return { label: t('severityInfo'), bg: 'rgba(10,132,255,0.14)', fg: '#0a6ed1' };
      case 'notify':
        return { label: t('severityNotify'), bg: 'rgba(48,160,88,0.16)', fg: '#1f8a4c' };
      default:
        return { label: severity || t('severityNotify'), bg: 'rgba(48,160,88,0.16)', fg: '#1f8a4c' };
    }
  };

  const handleMarkReadMini = async (notificationId) => {
    if (!notificationId || markingNotificationId) return;
    try {
      setMarkingNotificationId(notificationId);
      // optimistic update
      setUnreadNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setNotificationsCount((prev) => Math.max(0, (prev || 0) - 1));
      // Токен добавляется автоматически
      await api.patch(`${API_BASE_URL}notification/${notificationId}`, { read_status: 'read' });
    } catch (e) {
      // on error, refresh from server to be consistent
      await fetchMiniNotifications();
    } finally {
      setMarkingNotificationId(null);
    }
  };

  const handleIntervalChange = (event) => {
    const newInterval = event.target.value;
    setSelectedInterval(newInterval);
    
    // Уведомляем родительский компонент об изменении интервала
    if (onRefresh && typeof onRefresh.setRefreshInterval === 'function') {
      onRefresh.setRefreshInterval(newInterval);
    }
  };

  const handleManualRefresh = () => {
    // Вызываем функцию обновления данных
    if (onRefresh && typeof onRefresh.refreshData === 'function') {
      onRefresh.refreshData();
    }
  };

  const handleNavigateToDashboard = () => {
    if (isClientBlocked) {
      navigate('/bill');
      return;
    }
    navigate('/dashboard');
  };

  const handleNavigateToUser = () => {
    // Получаем user_id из auth context
    if (user && user.id) {
      navigate(`/profile/${user.id}`);
      return;
    }
    // Fallback if user data not available
    console.warn('User data not available in auth context');
    navigate('/profile/default');
  };

  const handleLogout = async () => {
    try {
      await authLogout();
      // AuthContext handles navigation
    } catch (error) {
      console.error('Logout error:', error);
      // AuthContext handles cleanup
    }
  };

  const intervalOptions = [
    { value: 60000, label: t('interval_1m') },
    { value: 180000, label: t('interval_3m') },
    { value: 600000, label: t('interval_10m') },
    { value: 1800000, label: t('interval_30m') },
    { value: 3600000, label: t('interval_1h') }
  ];

  // Синхронизируем selectedInterval с refreshInterval из props
  useEffect(() => {
    setSelectedInterval(refreshInterval);
  }, [refreshInterval]);

  // Загружаем мини-уведомления при монтировании
  useEffect(() => {
    fetchMiniNotifications();
  }, []);

  // Глобальный обработчик кликов для закрытия меню обновления
  useEffect(() => {
    const handleGlobalClick = (event) => {
      if (isRefreshMenuOpen) {
        // Проверяем, что клик не был на элементах меню обновления
        const refreshMenuElement = event.target.closest('[data-refresh-menu]');
        const refreshButton = event.target.closest('[data-refresh-button]');
        
        if (!refreshMenuElement && !refreshButton) {
          setIsRefreshMenuOpen(false);
        }
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [isRefreshMenuOpen]);

  return (
    <AppBar
      position="static"
      style={{
        background: theme.palette.mode === 'light'
          ? 'linear-gradient(90deg, #1d0d88 0%, #2511A8 55%, #3a1fd0 100%)'
          : 'linear-gradient(90deg, #17151d 0%, #1b1922 100%)',
        boxShadow: '0 2px 14px -6px rgba(20, 10, 80, 0.45)',
      }}
    >
      <Toolbar>
        <img
          src="/logo-white.svg"
          alt="Logo"
          style={{ height: isMobile ? 30 : 40, marginRight: isMobile ? 8 : 16, cursor: 'pointer' }}
          onClick={handleNavigateToDashboard}
        />

        <HostingSwitcher />

        {/* Разделы словами (десктоп). Состав зависит от роли. */}
        {!isMobile && (() => {
          const navSx = (active) => ({
            color: active ? '#fff' : 'rgba(255,255,255,0.72)',
            backgroundColor: active ? 'rgba(255,255,255,0.16)' : 'transparent',
            textTransform: 'none',
            fontWeight: active ? 600 : 500,
            fontSize: 14,
            px: 1.25,
            minWidth: 0,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff' },
          });
          const at = (path) => location.pathname.startsWith(path);
          const billsPath = isHosterOrAbove(user?.role) ? '/payments' : '/bill';
          const adminPaths = ['/users', '/access-control', '/requests', '/models-settings', '/outages'];
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 2 }}>
              <Button sx={navSx(at('/dashboard') || at('/device'))} onClick={handleNavigateToDashboard}>{t('navAsics')}</Button>
              {!isClientBlocked && <Button sx={navSx(at('/tickets'))} onClick={() => navigate('/tickets')}>{t('navTickets')}</Button>}
              {!isClientBlocked && <Button sx={navSx(at('/alerts'))} onClick={() => navigate('/alerts')}>{t('navAlerts')}</Button>}
              <Button sx={navSx(at('/payments') || at('/bill'))} onClick={() => navigate(billsPath)}>{t('navBills')}</Button>
              <AgentControl shouldShowButton={isHosterOrAbove(user?.role)} isMobile={false} navSx={navSx(false)} />
              {isAdminOrSuperAdmin(user?.role) && (
                <>
                  <Button
                    sx={navSx(adminPaths.some(at))}
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={(e) => setAdminAnchorEl(e.currentTarget)}
                  >
                    {t('navAdmin')}
                  </Button>
                  <MuiMenu anchorEl={adminAnchorEl} open={Boolean(adminAnchorEl)} onClose={() => setAdminAnchorEl(null)}>
                    <MenuItem onClick={() => { setAdminAnchorEl(null); navigate('/users'); }}>{t('userManagement')}</MenuItem>
                    <MenuItem onClick={() => { setAdminAnchorEl(null); navigate('/access-control'); }}>{t('accessControl')}</MenuItem>
                    <MenuItem onClick={() => { setAdminAnchorEl(null); navigate('/requests'); }}>{t('userRequests')}</MenuItem>
                    <MenuItem onClick={() => { setAdminAnchorEl(null); navigate('/models-settings'); }}>{t('equipmentSettings')}</MenuItem>
                    <MenuItem onClick={() => { setAdminAnchorEl(null); navigate('/outages'); }}>{t('outageJournal')}</MenuItem>
                  </MuiMenu>
                </>
              )}
            </Box>
          );
        })()}

        <div style={{ flexGrow: 1 }}></div>

        {isMobile && (
          <IconButton style={{ marginRight: 0 }} onClick={handleNavigateToDashboard}>
            <HomeIcon style={{ fontSize: 24, color: 'white' }} />
          </IconButton>
        )}

        <Box 
          sx={{ 
            display: hideRefresh ? 'none' : 'flex', 
            alignItems: 'center', 
            marginRight: 0,
            position: 'relative'
          }}
        >
          {/* Элементы обновления появляются только при клике */}
          {isRefreshMenuOpen && onRefresh && (
            <Box
              data-refresh-menu
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              sx={{
                position: 'absolute',
                top: '100%',
                right: 0,
                zIndex: 9998,
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1,
                boxShadow: theme.shadows[4],
                padding: 2,
                minWidth: isMobile ? 160 : 200,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                animation: 'fadeIn 0.2s ease-in-out',
                '@keyframes fadeIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(-10px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 0, 
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                {t('refreshRate')}
              </Typography>
              <select
                 style={{
                   width: '100%',
                   padding: '8px 12px',
                   borderRadius: '8px',
                   border: `1px solid ${theme.palette.divider}`,
                   backgroundColor: 'transparent',
                   color: theme.palette.text.primary,
                   fontSize: '0.9rem',
                   cursor: 'pointer',
                   appearance: 'none',
                   '-webkit-appearance': 'none',
                   '-moz-appearance': 'none',
                   backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20class%3D%22lucide%20lucide-chevron-down%22%3E%3Cpath%20d%3D%22m6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E")`,
                   backgroundRepeat: 'no-repeat',
                   backgroundPosition: 'right 12px center',
                   backgroundSize: '20px',
                   paddingRight: '30px',
                   transition: 'all 0.2s ease-in-out',
                   marginTop: '0px',
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.borderColor = theme.palette.primary.main;
                   e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.borderColor = theme.palette.divider;
                   e.target.style.backgroundColor = 'transparent';
                 }}
                 onFocus={(e) => {
                   e.target.style.borderColor = theme.palette.primary.main;
                   e.target.style.outline = 'none';
                   e.target.style.boxShadow = `0 0 0 2px ${theme.palette.primary.main}20`;
                 }}
                 onBlur={(e) => {
                   e.target.style.borderColor = theme.palette.divider;
                   e.target.style.boxShadow = 'none';
                 }}
                 value={selectedInterval}
                 onChange={handleIntervalChange}
                 onClick={(event) => {
                   event.stopPropagation();
                 }}
                 onMouseDown={(event) => {
                   event.stopPropagation();
                 }}
               >
                {intervalOptions.map((option) => (
                  <option key={option.value} value={option.value} style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              <Tooltip title={t('refreshNow')}>
                <IconButton 
                  onClick={handleManualRefresh}
                  sx={{ 
                    color: theme.palette.primary.main,
                    borderRadius: 2,
                    minWidth: 40,
                    height: 40,
                    border: `2px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    }
                  }}
                >
                  <RestartAltOutlinedIcon style={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          
          {/* Сообщение когда обновление недоступно */}
          {isRefreshMenuOpen && !onRefresh && (
            <Box
              data-refresh-menu
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              sx={{
                position: 'absolute',
                top: '100%',
                right: 0,
                zIndex: 9998,
                backgroundColor: 'transparent',
                borderRadius: 1,
                boxShadow: 'none',
                padding: 2,
                minWidth: 200,
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.text.secondary,
                animation: 'fadeIn 0.2s ease-in-out',
                '@keyframes fadeIn': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(-10px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {t('refreshUnavailable')}
            </Box>
          )}
          
          <IconButton 
            onClick={handleRefreshMenuToggle}
            disabled={!onRefresh}
            data-refresh-button
          >
            <RestartAltOutlinedIcon style={{ fontSize: 24, color: 'white' }} />
          </IconButton>
        </Box>
        
        <IconButton 
          style={{ marginRight: 0, position: 'relative' }} 
          onClick={handleNotificationsMenuOpen}
        >
          <NotificationsIcon style={{ fontSize: 24, color: 'white' }} />
          {/* Бейдж с количеством уведомлений */}
          {notificationsCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                right: -2,
                backgroundColor: theme.palette.error.main,
                color: 'white',
                borderRadius: '50%',
                width: 13.3333,
                height: 13.3333,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.5rem',
                fontWeight: 'bold',
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            >
              {notificationsCount > 99 ? '99+' : notificationsCount}
            </Box>
          )}
        </IconButton>
        {!isMobile && (
          <IconButton style={{ marginRight: 0 }} onClick={toggleTheme}>
            <Brightness4OutlinedIcon style={{ fontSize: 24, color: 'white' }} />
          </IconButton>
        )}

        <Button
          onClick={(e) => setLangAnchorEl(e.currentTarget)}
          endIcon={<KeyboardArrowDownIcon style={{ color: 'white' }} />}
          style={{ marginRight: 0, color: 'white', textTransform: 'none', minWidth: 0 }}
        >
          <span style={{ fontSize: 18, marginRight: isMobile ? 0 : 6, lineHeight: 1 }}>
            {(languageOptions.find((l) => i18n.language && i18n.language.startsWith(l.code)) || languageOptions[0]).flag}
          </span>
          {!isMobile && (languageOptions.find((l) => i18n.language && i18n.language.startsWith(l.code)) || languageOptions[0]).label}
        </Button>
        <MuiMenu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={() => setLangAnchorEl(null)}
        >
          {languageOptions.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={i18n.language === lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setLangAnchorEl(null);
                if (user?.id) {
                  saveLanguageToProfile(user.id, lang.code);
                  const cached = secureStorage.getUserData();
                  if (cached) {
                    secureStorage.setUserData({ ...cached, language: lang.code });
                  }
                }
              }}
              style={{ gap: 8 }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>{lang.flag}</span>
              {lang.label}
            </MenuItem>
          ))}
        </MuiMenu>

        <IconButton onClick={handleAvatarClick} style={{ marginLeft: isMobile ? 5 : 15 }}>
          <Avatar style={{ borderRadius: 5 }} />
        </IconButton>
        <MuiMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem disabled>{user?.name || t('defaultName')}</MenuItem>
          <MenuItem disabled>{user?.email || 'user@mail.com'}</MenuItem>
          <Divider />
          {!isClientBlocked && (
            <MenuItem onClick={handleNavigateToSettings}>
              <SettingsIcon style={{ marginRight: 10 }} /> {t('settings')}
            </MenuItem>
          )}
          <MenuItem onClick={() => { handleMenuClose(); handleNavigateToUser(); }}>
            <PersonIcon style={{ marginRight: 10 }} /> {t('profile')}
          </MenuItem>
          {!isClientBlocked && (
            <MenuItem onClick={handleNavigateToSubscription}>
              <CardMembershipIcon style={{ marginRight: 10 }} /> {t('subscription')}
            </MenuItem>
          )}
          <MenuItem onClick={handleNavigateToBill}>
            <ReceiptIcon style={{ marginRight: 10 }} /> {t('billing')}
          </MenuItem>
          {isHosterOrAbove(user?.role) && (
            <MenuItem onClick={handleNavigateToPayments}>
              <ReceiptLongIcon style={{ marginRight: 10 }} /> {t('billing:issueInvoices')}
            </MenuItem>
          )}
          {!isClientBlocked && (
            <MenuItem onClick={handleNavigateToTickets}>
              <AssignmentIcon style={{ marginRight: 10 }} /> {t('tickets')}
            </MenuItem>
          )}
          <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>
            <ExitToAppIcon style={{ marginRight: 10 }} /> {t('logout')}
          </MenuItem>
        </MuiMenu>

        {/* Меню уведомлений */}
        <MuiMenu
          anchorEl={notificationsAnchorEl}
          open={Boolean(notificationsAnchorEl)}
          onClose={handleNotificationsMenuClose}
          PaperProps={{
            sx: {
              minWidth: isMobile ? 240 : 320,
              maxWidth: isMobile ? '92vw' : 380,
              maxHeight: 440,
              overflow: 'auto',
              borderRadius: '10px',
              mt: 1
            }
          }}
        >
          <MenuItem disabled sx={{ 
            backgroundColor: theme.palette.action.hover,
            fontWeight: 600,
            fontSize: '0.9rem'
          }}>
            {t('notifications')}
          </MenuItem>
          <Divider />

          {notificationsCount === 0 ? (
            <MenuItem disabled sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.85rem',
              fontStyle: 'italic'
            }}>
              {t('noNewNotifications')}
            </MenuItem>
          ) : (
            <MenuItem disabled sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.85rem'
            }}>
              {t('notificationCount', { count: notificationsCount })}
            </MenuItem>
          )}
          
          <Divider />

          {unreadNotifications.map((n) => {
            const chipProps = getSeverityChipProps(n.severity);
            const isMarking = markingNotificationId === n.id;
            return (
              <MenuItem 
                key={n.id} 
                onClick={() => handleMarkReadMini(n.id)}
                sx={{ alignItems: 'flex-start', cursor: isMarking ? 'default' : 'pointer', opacity: isMarking ? 0.6 : 1 }}
                disabled={isMarking}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500, flex: 1, whiteSpace: 'normal', lineHeight: 1.3 }}>
                      {n.subject}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        backgroundColor: chipProps.bg,
                        color: chipProps.fg,
                        fontSize: '10.5px',
                        fontWeight: 600,
                        lineHeight: 1.4,
                        px: 0.9,
                        py: '2px',
                        borderRadius: '6px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {chipProps.label}
                    </Box>
                  </Box>
                </Box>
              </MenuItem>
            );
          })}

          {unreadNotifications.length > 0 && <Divider />}
          <MenuItem onClick={handleNavigateToLogs} sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 500
          }}>
            <ReceiptIcon style={{ marginRight: 10, fontSize: 20 }} />
            {t('allNotifications')}
          </MenuItem>
        </MuiMenu>
      </Toolbar>
      
      {/* Popup для виджетов */}
      <Dialog
        open={isWidgetsPopupOpen}
        onClose={handleWidgetsPopupClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 24,
          }
        }}
        BackdropProps={{
          onClick: handleWidgetsPopupClose
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: 2
          }}
        >
          <Typography variant="h6" component="div">
            {t('widgetsPanel')}
          </Typography>
          <IconButton
            onClick={handleWidgetsPopupClose}
            size="small"
            sx={{ color: theme.palette.text.secondary }}
          >
            <ExitToAppIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {t('widgetsWelcome')}
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('availableFeatures')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 2 }}>
              {isAdminOrSuperAdmin(user?.role) && (
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => {
                    handleWidgetsPopupClose();
                    navigate('/users');
                  }}
                  title={t('userManagement')}
                >
                  <Typography variant="subtitle1">{t('userManagement')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('userManagementDesc')}</Typography>
                </Paper>
              )}
              {isAdminOrSuperAdmin(user?.role) && (
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => {
                    handleWidgetsPopupClose();
                    navigate('/access-control');
                  }}
                  title={t('accessControl')}
                >
                  <Typography variant="subtitle1">{t('accessControl')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('accessControlDesc')}</Typography>
                </Paper>
              )}
              {isAdminOrSuperAdmin(user?.role) && (
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => {
                    handleWidgetsPopupClose();
                    navigate('/requests');
                  }}
                  title={t('userRequests')}
                >
                  <Typography variant="subtitle1">{t('userRequests')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('userRequestsDesc')}</Typography>
                </Paper>
              )}
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                  transition: 'background-color 0.2s ease'
                }}
                onClick={() => {
                  handleWidgetsPopupClose();
                  navigate('/tickets');
                }}
                title={t('equipmentTickets')}
              >
                <Typography variant="subtitle1">{t('equipmentTickets')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('equipmentTicketsDesc')}</Typography>
              </Paper>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                  transition: 'background-color 0.2s ease'
                }}
                onClick={() => {
                  handleWidgetsPopupClose();
                  navigate('/alerts');
                }}
                title={t('alertsAndNotifications')}
              >
                <Typography variant="subtitle1">{t('alertsAndNotifications')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('alertsAndNotificationsDesc')}</Typography>
              </Paper>

              {isHosterOrAbove(user?.role) && (
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => {
                    handleWidgetsPopupClose();
                    navigate('/payments');
                  }}
                  title={t('paymentsAndBills')}
                >
                  <Typography variant="subtitle1">{t('paymentsAndBills')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('paymentsAndBillsDesc')}</Typography>
                </Paper>
              )}

              {/* Настройка оборудования - только для admin/super_admin */}
              {isAdminOrSuperAdmin(user?.role) && (
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    transition: 'background-color 0.2s ease'
                  }}
                  onClick={() => {
                    handleWidgetsPopupClose();
                    navigate('/models-settings');
                  }}
                  title={t('equipmentSettings')}
                >
                  <Typography variant="subtitle1">{t('equipmentSettings')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('equipmentSettingsDesc')}</Typography>
                </Paper>
              )}

            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleWidgetsPopupClose} variant="outlined">
            {t('common:close')}
          </Button>
          <Button variant="contained" color="primary">
            {t('applySettings')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Menu;
